/**
 * @fileoverview Runner Leaderboard Component
 * @description Shows best scores leaderboard for Endless Runner
 */

import React, { memo, useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
} from "react-native";
import { Image } from "expo-image";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import { RunnerDifficulty, RUNNER_DIFFICULTY_CONFIGS, CharacterId } from "../types";

// Character images
const OPA_IMAGE = require("@/assets/games/opa_endless_runner.svg");
const OMA_IMAGE = require("@/assets/games/omam_endless_runner.svg");

interface LeaderboardEntry {
  rank: number;
  user_id: string;
  full_name: string;
  avatar_url: string | null;
  best_score: number;
  games_played: number;
  total_score: number;
  best_score_character?: CharacterId;
}

interface RunnerLeaderboardProps {
  /** Mode to show leaderboard for */
  mode?: "free" | "weekend";
  /** Maximum entries to show */
  maxEntries?: number;
}

/**
 * Format number with commas
 */
const formatNumber = (num: number): string => {
  return num.toLocaleString();
};

/**
 * Rank badge component
 */
const RankBadge: React.FC<{ rank: number }> = ({ rank }) => {
  const colors = useThemeColors();

  if (rank === 1) {
    return (
      <View style={[styles.rankBadge, { backgroundColor: "#FFD70030" }]}>
        <Text style={styles.rankEmoji}>🥇</Text>
      </View>
    );
  }
  if (rank === 2) {
    return (
      <View style={[styles.rankBadge, { backgroundColor: "#C0C0C030" }]}>
        <Text style={styles.rankEmoji}>🥈</Text>
      </View>
    );
  }
  if (rank === 3) {
    return (
      <View style={[styles.rankBadge, { backgroundColor: "#CD7F3230" }]}>
        <Text style={styles.rankEmoji}>🥉</Text>
      </View>
    );
  }

  return (
    <View style={[styles.rankBadge, { backgroundColor: colors.surfaceSecondary }]}>
      <Text style={[styles.rankNumber, { color: colors.textSecondary }]}>
        {rank}
      </Text>
    </View>
  );
};

/**
 * RunnerLeaderboard Component
 */
export const RunnerLeaderboard = memo<RunnerLeaderboardProps>(
  ({ mode = "free", maxEntries = 5 }) => {
    const colors = useThemeColors();
    const { activeProfile } = useAuth();

    const [selectedDifficulty, setSelectedDifficulty] =
      useState<RunnerDifficulty>("medium");
    const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [myEntry, setMyEntry] = useState<LeaderboardEntry | null>(null);

    /**
     * Fetch leaderboard data
     */
    const fetchLeaderboard = useCallback(async () => {
      setIsLoading(true);

      try {
        const { data, error } = await supabase
          .from("user_game_stats")
          .select(
            `
            user_id,
            stats,
            personal_best_score,
            user:profiles!user_game_stats_user_id_fkey(
              id,
              full_name,
              avatar_url
            )
          `
          )
          .eq("game_slug", "endless_runner")
          .eq("mode", mode)
          .not("personal_best_score", "is", null)
          .order("personal_best_score", { ascending: false })
          .limit(50);

        if (error) throw error;

        // Filter and sort by difficulty best score
        const entries: LeaderboardEntry[] = [];

        for (const entry of data || []) {
          const stats = entry.stats as Record<string, any> | null;
          if (!stats) continue;

          const diffStats = stats[selectedDifficulty];
          if (!diffStats?.best_score) continue;

          const userData = entry.user;
          const userProfile =
            userData && typeof userData === "object" && !Array.isArray(userData)
              ? (userData as { id: string; full_name: string; avatar_url: string | null })
              : null;

          entries.push({
            rank: 0,
            user_id: entry.user_id,
            full_name: userProfile?.full_name || "Unknown",
            avatar_url: userProfile?.avatar_url || null,
            best_score: diffStats.best_score,
            games_played: diffStats.games_played || 0,
            total_score: diffStats.total_score || 0,
            best_score_character: diffStats.best_score_character,
          });
        }

        // Sort by best score
        entries.sort((a, b) => b.best_score - a.best_score);

        // Assign ranks
        entries.forEach((entry, index) => {
          entry.rank = index + 1;
        });

        // Limit entries
        const displayEntries = entries.slice(0, maxEntries);
        setLeaderboard(displayEntries);

        // Find current user
        if (activeProfile?.id) {
          const myRank = entries.find((e) => e.user_id === activeProfile.id);
          setMyEntry(myRank || null);
        }
      } catch (err) {
        console.error("[RunnerLeaderboard] Error:", err);
        setLeaderboard([]);
      } finally {
        setIsLoading(false);
      }
    }, [selectedDifficulty, mode, maxEntries, activeProfile?.id]);

    useEffect(() => {
      fetchLeaderboard();
    }, [fetchLeaderboard]);

    // Check if current user is in displayed leaderboard
    const isMyEntryInLeaderboard =
      myEntry && leaderboard.some((e) => e.user_id === activeProfile?.id);

    return (
      <View style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Ionicons 
              name={mode === "weekend" ? "trophy" : "podium"} 
              size={20} 
              color={mode === "weekend" ? "#F59E0B" : colors.primary} 
            />
            <Text style={[styles.title, { color: colors.text }]}>
              {mode === "weekend" ? "Weekend Ranglijst" : "Hoogste Scores"}
            </Text>
          </View>
          {mode === "weekend" && (
            <View style={[styles.weekendBadge, { backgroundColor: "#F59E0B20" }]}>
              <Text style={[styles.weekendBadgeText, { color: "#F59E0B" }]}>
                Familie Weekend
              </Text>
            </View>
          )}
        </View>

        {/* Difficulty Tabs */}
        <View style={styles.tabs}>
          {(["easy", "medium", "hard"] as RunnerDifficulty[]).map((diff) => {
            const config = RUNNER_DIFFICULTY_CONFIGS[diff];
            const isSelected = selectedDifficulty === diff;

            return (
              <TouchableOpacity
                key={diff}
                onPress={() => setSelectedDifficulty(diff)}
                style={[
                  styles.tab,
                  {
                    backgroundColor: isSelected
                      ? `${config.color}20`
                      : colors.surfaceSecondary,
                    borderColor: isSelected ? config.color : "transparent",
                  },
                ]}
              >
                <View
                  style={[styles.tabDot, { backgroundColor: config.color }]}
                />
                <Text
                  style={[
                    styles.tabText,
                    { color: isSelected ? config.color : colors.textSecondary },
                  ]}
                >
                  {config.label}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>

        {/* Loading */}
        {isLoading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="small" color={colors.primary} />
          </View>
        ) : leaderboard.length === 0 ? (
          /* Empty State */
          <View
            style={[styles.emptyContainer, { backgroundColor: colors.surface }]}
          >
            <Ionicons name="trophy-outline" size={32} color={colors.textTertiary} />
            <Text style={[styles.emptyText, { color: colors.textSecondary }]}>
              Nog geen scores geregistreerd
            </Text>
            <Text style={[styles.emptySubtext, { color: colors.textTertiary }]}>
              Wees de eerste om een score neer te zetten!
            </Text>
          </View>
        ) : (
          /* Leaderboard Entries */
          <View
            style={[styles.entriesContainer, { backgroundColor: colors.surface }]}
          >
            {leaderboard.map((entry) => {
              const isCurrentUser = entry.user_id === activeProfile?.id;

              return (
                <View
                  key={entry.user_id}
                  style={[
                    styles.row,
                    isCurrentUser && {
                      backgroundColor: `${colors.primary}15`,
                      borderRadius: 8,
                    },
                  ]}
                >
                  <RankBadge rank={entry.rank} />

                  {/* Avatar */}
                  <View
                    style={[styles.avatar, { backgroundColor: colors.primaryLight }]}
                  >
                    {entry.avatar_url ? (
                      <Image
                        source={{ uri: entry.avatar_url }}
                        style={styles.avatarImage}
                      />
                    ) : (
                      <Text style={[styles.avatarText, { color: colors.primary }]}>
                        {(entry.full_name || "?").charAt(0).toUpperCase()}
                      </Text>
                    )}
                  </View>

                  {/* Name */}
                  <View style={styles.nameContainer}>
                    <View style={styles.nameRow}>
                      <Text
                        style={[
                          styles.name,
                          { color: colors.text },
                          isCurrentUser && { fontWeight: "700" },
                        ]}
                        numberOfLines={1}
                      >
                        {entry.full_name}
                      </Text>
                      {isCurrentUser && (
                        <View
                          style={[styles.youBadge, { backgroundColor: colors.primary }]}
                        >
                          <Text style={styles.youBadgeText}>Jij</Text>
                        </View>
                      )}
                    </View>
                    <Text style={[styles.stat, { color: colors.textSecondary }]}>
                      {entry.games_played} spellen
                    </Text>
                  </View>

                  {/* Character used for best score */}
                  {entry.best_score_character && (
                    <View style={styles.characterBadge}>
                      <Image
                        source={entry.best_score_character === "opa" ? OPA_IMAGE : OMA_IMAGE}
                        style={styles.characterImage}
                        contentFit="contain"
                      />
                    </View>
                  )}

                  {/* Score */}
                  <View style={styles.scoreContainer}>
                    <Text style={[styles.score, { color: "#F59E0B" }]}>
                      {formatNumber(entry.best_score)}
                    </Text>
                  </View>
                </View>
              );
            })}

            {/* Current user if not in top */}
            {myEntry && !isMyEntryInLeaderboard && (
              <>
                <View style={[styles.separator, { backgroundColor: colors.border }]}>
                  <Text style={[styles.separatorDots, { color: colors.textTertiary }]}>
                    • • •
                  </Text>
                </View>
                <View
                  style={[
                    styles.row,
                    {
                      backgroundColor: `${colors.primary}15`,
                      borderRadius: 8,
                    },
                  ]}
                >
                  <RankBadge rank={myEntry.rank} />
                  <View
                    style={[styles.avatar, { backgroundColor: colors.primaryLight }]}
                  >
                    {myEntry.avatar_url ? (
                      <Image
                        source={{ uri: myEntry.avatar_url }}
                        style={styles.avatarImage}
                      />
                    ) : (
                      <Text style={[styles.avatarText, { color: colors.primary }]}>
                        {(myEntry.full_name || "?").charAt(0).toUpperCase()}
                      </Text>
                    )}
                  </View>
                  <View style={styles.nameContainer}>
                    <View style={styles.nameRow}>
                      <Text
                        style={[styles.name, { color: colors.text, fontWeight: "700" }]}
                        numberOfLines={1}
                      >
                        {myEntry.full_name}
                      </Text>
                      <View
                        style={[styles.youBadge, { backgroundColor: colors.primary }]}
                      >
                        <Text style={styles.youBadgeText}>Jij</Text>
                      </View>
                    </View>
                    <Text style={[styles.stat, { color: colors.textSecondary }]}>
                      {myEntry.games_played} spellen
                    </Text>
                  </View>
                  {/* Character used for best score */}
                  {myEntry.best_score_character && (
                    <View style={styles.characterBadge}>
                      <Image
                        source={myEntry.best_score_character === "opa" ? OPA_IMAGE : OMA_IMAGE}
                        style={styles.characterImage}
                        contentFit="contain"
                      />
                    </View>
                  )}
                  <View style={styles.scoreContainer}>
                    <Text style={[styles.score, { color: "#F59E0B" }]}>
                      {formatNumber(myEntry.best_score)}
                    </Text>
                  </View>
                </View>
              </>
            )}
          </View>
        )}
      </View>
    );
  }
);

RunnerLeaderboard.displayName = "RunnerLeaderboard";

const styles = StyleSheet.create({
  container: {
    gap: 12,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  title: {
    fontSize: 16,
    fontWeight: "600",
  },
  tabs: {
    flexDirection: "row",
    gap: 8,
  },
  tab: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    gap: 6,
  },
  tabDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  tabText: {
    fontSize: 13,
    fontWeight: "500",
  },
  loadingContainer: {
    padding: 32,
    alignItems: "center",
  },
  emptyContainer: {
    padding: 24,
    borderRadius: 12,
    alignItems: "center",
    gap: 8,
  },
  emptyText: {
    fontSize: 14,
    fontWeight: "500",
  },
  emptySubtext: {
    fontSize: 12,
  },
  entriesContainer: {
    borderRadius: 12,
    padding: 8,
    gap: 4,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    padding: 8,
  },
  rankBadge: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
  },
  rankEmoji: {
    fontSize: 14,
  },
  rankNumber: {
    fontSize: 12,
    fontWeight: "600",
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
    overflow: "hidden",
  },
  avatarImage: {
    width: "100%",
    height: "100%",
  },
  avatarText: {
    fontSize: 14,
    fontWeight: "700",
  },
  nameContainer: {
    flex: 1,
  },
  nameRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  name: {
    fontSize: 14,
    fontWeight: "500",
    flexShrink: 1,
  },
  youBadge: {
    paddingHorizontal: 5,
    paddingVertical: 1,
    borderRadius: 4,
  },
  youBadgeText: {
    fontSize: 9,
    fontWeight: "700",
    color: "#FFFFFF",
  },
  stat: {
    fontSize: 11,
    marginTop: 2,
  },
  scoreContainer: {
    alignItems: "flex-end",
  },
  score: {
    fontSize: 16,
    fontWeight: "700",
  },
  characterBadge: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: "#F3F4F6",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 8,
    overflow: "hidden",
  },
  characterImage: {
    width: 22,
    height: 22,
  },
  separator: {
    alignItems: "center",
    paddingVertical: 6,
  },
  separatorDots: {
    fontSize: 10,
  },
  weekendBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  weekendBadgeText: {
    fontSize: 11,
    fontWeight: "600",
  },
});

